import { z } from 'zod';
import { isoDate, reason, timeOfDay, timeZone, uuid } from './schemas.js';

export const PUNCH_TYPES = ['in', 'out', 'punch'] as const;
export type PunchType = (typeof PUNCH_TYPES)[number];
export const PUNCH_SOURCES = ['web', 'qr', 'device', 'import', 'admin', 'agent'] as const;
export type PunchSource = (typeof PUNCH_SOURCES)[number];
export const PUNCH_SOURCE_LABELS: Record<PunchSource, string> = {
  web: 'App',
  qr: 'Door QR',
  device: 'Fingerprint',
  import: 'Imported',
  admin: 'Added by admin',
  agent: 'Desktop agent',
};

export const ATTENDANCE_STATUSES = [
  'present',
  'absent',
  'leave',
  'holiday',
  'day_off',
  'incomplete',
  'in_progress',
  'not_started',
  'not_employed',
  'not_tracked',
  'future',
] as const;
export type AttendanceStatus = (typeof ATTENDANCE_STATUSES)[number];
export const ATTENDANCE_STATUS_LABELS: Record<AttendanceStatus, string> = {
  present: 'Present',
  absent: 'Absent',
  leave: 'On leave',
  holiday: 'Public holiday',
  day_off: 'Day off',
  incomplete: 'Missing punch',
  in_progress: 'Working',
  not_started: 'Not yet in',
  not_employed: 'Not employed',
  not_tracked: 'Not tracked',
  future: '—',
};

export const DEVICE_KINDS = ['fingerprint', 'qr_kiosk', 'api'] as const;
export type DeviceKind = (typeof DEVICE_KINDS)[number];

// ---------------------------------------------------------------------------
// Inputs
// ---------------------------------------------------------------------------
export const clockSchema = z.object({ direction: z.enum(['in', 'out']) }).strict();
export const scanSchema = z.object({ token: z.string().min(10).max(300), direction: z.enum(['in', 'out']) }).strict();

export const adminPunchSchema = z
  .object({
    employeeId: uuid,
    /** Local date and time in the employee's time zone. */
    date: isoDate,
    time: timeOfDay,
    eventType: z.enum(['in', 'out']),
    reason,
  })
  .strict();

export const voidPunchSchema = z.object({ reason }).strict();

const cidr = z
  .string()
  .trim()
  .regex(/^[0-9a-fA-F:.]+(\/\d{1,3})?$/, 'Use an IP address or range, e.g. 41.33.10.20 or 41.33.10.0/24');

export const deviceSchema = z
  .object({
    name: z.string().trim().min(1).max(100),
    kind: z.enum(DEVICE_KINDS),
    vendor: z.string().trim().max(100).nullable().optional(),
    model: z.string().trim().max(100).nullable().optional(),
    serialNumber: z
      .string()
      .trim()
      .max(64)
      .regex(/^[A-Za-z0-9_-]*$/, 'Letters and digits only')
      .transform((s) => (s === '' ? null : s))
      .nullable()
      .optional(),
    timezone: timeZone,
    trustPunchType: z.boolean().default(false),
    allowedNetworks: z.array(cidr).max(20).nullable().optional(),
    isActive: z.boolean().optional(),
  })
  .strict();
export type DeviceInput = z.infer<typeof deviceSchema>;

export const importSchema = z
  .object({
    deviceId: uuid.nullable().optional(),
    fileName: z.string().max(255).optional(),
    /** CSV / TXT (e.g. attlog.dat) content. */
    content: z.string().min(1).max(10_000_000).optional(),
    /** Or an Excel file (.xls / .xlsx), base64 encoded. */
    fileBase64: z.string().min(1).max(14_000_000).optional(),
    /** Time zone of the times in the file (defaults to the device's). */
    timezone: timeZone.optional(),
    dryRun: z.boolean().default(false),
  })
  .strict()
  .refine((b) => !!b.content || !!b.fileBase64, { message: 'Choose a file', path: ['content'] });

/** Generic push API for devices/integrations. */
export const pushSchema = z
  .object({
    punches: z
      .array(
        z
          .object({
            pin: z.string().min(1).max(32),
            /** ISO timestamp with offset, or local "YYYY-MM-DD HH:MM:SS" in the device time zone. */
            time: z.string().min(16).max(40),
            type: z.enum(PUNCH_TYPES).optional(),
            id: z.string().max(100).optional(),
          })
          .strict(),
      )
      .min(1)
      .max(5000),
  })
  .strict();

export const attendanceRangeSchema = z.object({ from: isoDate, to: isoDate });

// ---------------------------------------------------------------------------
// DTOs
// ---------------------------------------------------------------------------
export interface PunchDto {
  id: string;
  employeeId: string;
  occurredAt: string;
  eventType: PunchType;
  /** The direction used in calculations (resolved for "punch"). */
  resolvedType: 'in' | 'out';
  source: PunchSource;
  deviceName: string | null;
  note: string | null;
  createdBy: string | null;
  voidedAt: string | null;
  voidedBy: string | null;
  voidReason: string | null;
}

export interface AttendanceDayDto {
  employeeId: string;
  employeeName?: string;
  employeeCode?: string;
  date: string;
  timezone: string;
  status: AttendanceStatus;
  isWorkday: boolean;
  scheduledStart: string | null;
  scheduledEnd: string | null;
  scheduledMinutes: number;
  /** Schedule for the day after public holidays but before leave (basis for payroll hours). */
  requiredMinutes: number;
  workedMinutes: number;
  differenceMinutes: number;
  lateMinutes: number;
  earlyLeaveMinutes: number;
  firstIn: string | null;
  lastOut: string | null;
  missingIn: boolean;
  missingOut: boolean;
  punchCount: number;
  leaveFraction: number;
  holidayName: string | null;
  calcMode: 'first_last' | 'sum_intervals';
  /** 'computer' = worked time and first/last come from the desktop agent (remote staff who do not clock in). */
  hoursSource?: 'attendance' | 'computer';
  locked: boolean;
  punches?: PunchDto[];
}

export interface AttendanceTotals {
  scheduledMinutes: number;
  workedMinutes: number;
  differenceMinutes: number;
  lateDays: number;
  lateMinutes: number;
  earlyLeaveDays: number;
  absentDays: number;
  missingPunchDays: number;
  presentDays: number;
  leaveDays: number;
}

export interface ClockStatusDto {
  today: AttendanceDayDto;
  clockedIn: boolean;
  lastPunch: PunchDto | null;
  canUseAppButton: boolean;
  appButtonReason: string | null;
}

export interface DeviceDto {
  id: string;
  name: string;
  kind: DeviceKind;
  vendor: string | null;
  model: string | null;
  serialNumber: string | null;
  timezone: string;
  trustPunchType: boolean;
  allowedNetworks: string[] | null;
  isActive: boolean;
  hasKey: boolean;
  lastSeenAt: string | null;
  lastSeenIp: string | null;
  punchCount: number;
}

export interface ImportResultDto {
  batchId: string | null;
  totalRows: number;
  imported: number;
  duplicates: number;
  unknownPins: { pin: string; count: number; name?: string }[];
  errors: { line: number; message: string }[];
  preview: { pin: string; employeeName: string | null; occurredAt: string; type: PunchType }[];
  format: string;
  /** Punches dated inside a closed reporting period: kept in the log but they don't change the frozen figures. */
  inClosedPeriod?: number;
}
